<template>
    <transition
        :name="transitionName"
        v-on="$attrs">
        <div
            v-if="visible"
            ref="container"
            class="sbs-float-container trs"
            :class="containerClasses"
            :style="containerStyles">
            <slot></slot>
        </div>
    </transition>
</template>

<script>
import _cloneDeep from 'lodash/cloneDeep.js';
import ResizeObserver from '../../utils/resizeObserver.js';
import {
    converToUnit,
    getScrollbarWidth,
} from '../../utils/helpers.js';
import {
    AllPositions,
    FLOAT_POSITIONS,
} from '../../utils/positions.js';
import {
    AllTransitions,
    APP_TRANSITIONS,
} from '../../utils/transitions.js';
import {
    isArrayContainsValue,
    isValueConvertedToNumber,
} from '../../utils/validators.js';

const BOUNDARIES = {
    toggler: {
        top: null,
        left: null,
        width: null,
        height: null,
    },
    element: {
        width: null,
        height: null,
    },
    parent: {
        top: null,
        left: null,
        width: null,
        height: null,
        scrollTop: null,
        scrollLeft: null,
    },
};

export default {
    name: 'SbsFloatContainer',
    data() {
        return {
            boundaries: _cloneDeep(BOUNDARIES),
            containerCoords: {
                top: null,
                left: null,
            },
            isDeactivated: null,
            resizeObserver: null,
            scrollbarWidth: 0,
        };
    },
    computed: {
        initialContainerDOMElement() {
            return this.initialContainer || this.$el?.parentElement;
        },
        currentPosition() {
            return this.position.trim().split('-');
        },
        isTop() {
            return this.currentPosition.includes('top');
        },
        isRight() {
            return this.currentPosition.includes('right');
        },
        isBottom() {
            return this.currentPosition.includes('bottom');
        },
        isLeft() {
            return this.currentPosition.includes('left');
        },
        isAxisStart() {
            return this.currentPosition.includes('start');
        },
        isAxisEnd() {
            return this.currentPosition.includes('end');
        },
        computedOffset() {
            return converToUnit(this.offset);
        },
        containerClasses() {
            return [
                {
                    fixed: this.fixed,
                },
            ];
        },
        containerStyles() {
            if (this.isDeactivated) return {};

            const top = converToUnit(this.containerCoords.top);
            const left = converToUnit(this.containerCoords.left);
            const width = converToUnit(this.containerCoords.width);

            return {
                top: this.isTop || this.isBottom
                    ? `calc(${top} ${this.isTop ? '-' : '+'} ${this.computedOffset})`
                    : top,
                left: this.isLeft || this.isRight
                    ? `calc(${left} ${this.isLeft ? '-' : '+'} ${this.computedOffset})`
                    : left,
                zIndex: Number(this.zIndex),
                width,
            };
        },
    },
    methods: {
        getParentDOMElement() {
            const parent = this.parentSelector
                ? document.querySelector(this.parentSelector)
                : document.body;

            return parent || document.body;
        },
        resetBoundaries() {
            this.boundaries = _cloneDeep(BOUNDARIES);
            this.containerCoords.top = null;
            this.containerCoords.left = null;
        },
        calcBoundaries() {
            this.calcParentElementBoundaries();
            this.calcElWidth();
            this.calcContainerTogglerBoundaries();
            this.calcContainerCoords();
        },
        calcParentElementBoundaries() {
            const parentDOMElement = this.getParentDOMElement();

            if (!parentDOMElement) return;

            const {
                top,
                left,
            } = parentDOMElement.getBoundingClientRect();
            const {
                scrollTop,
                scrollLeft,
                scrollWidth,
                scrollHeight,
            } = parentDOMElement;

            this.boundaries.parent.top = this.fixed ? 0 : top;
            this.boundaries.parent.left = this.fixed ? 0 : left;
            this.boundaries.parent.width = scrollWidth;
            this.boundaries.parent.height = scrollHeight;
            this.boundaries.parent.scrollTop = this.fixed ? 0 : scrollTop;
            this.boundaries.parent.scrollLeft = this.fixed ? 0 : scrollLeft;
        },
        calcElWidth() {
            if (!this.$refs.container) return;

            const {
                width = 0,
                height = 0,
            } = this.$refs.container?.getBoundingClientRect() ?? {};

            this.boundaries.element.width = Math.ceil(width);
            this.boundaries.element.height = Math.ceil(height);
        },
        calcContainerTogglerBoundaries() {
            if (!this.toggler) return;

            const {
                top,
                left,
                width,
                height,
            } = this.toggler.getBoundingClientRect();

            this.boundaries.toggler.top =
                top +
                Number(this.boundaries.parent.scrollTop) -
                Number(this.boundaries.parent.top);
            this.boundaries.toggler.left =
                left +
                Number(this.boundaries.parent.scrollLeft) -
                Number(this.boundaries.parent.left);
            this.boundaries.toggler.width = width;
            this.boundaries.toggler.height = height;
        },
        // eslint-disable-next-line complexity
        calcContainerCoords() {
            const {
                top: togglerTop,
                left: togglerLeft,
                width: togglerWidth,
                height: togglerHeight,
            } = this.boundaries.toggler;
            const {
                width: elWidth,
                height: elHeight,
            } = this.boundaries.element;
            const {
                width: parentWidth,
                height: parentHeight,
                scrollLeft: parentScrollLeft,
                scrollTop: parentScrollTop,
            } = this.boundaries.parent;

            if (this.isLeft || this.isRight) {
                this.containerCoords.left = this.isLeft
                    ? togglerLeft - elWidth
                    : togglerLeft + togglerWidth;

                if (this.isAxisStart) {
                    this.containerCoords.top = togglerTop;
                } else if (this.isAxisEnd) {
                    this.containerCoords.top = togglerTop + togglerHeight - elHeight;
                } else {
                    this.containerCoords.top = togglerTop + (togglerHeight - elHeight) / 2;
                }

                if (this.containerCoords.top < parentScrollTop) {
                    this.containerCoords.top = parentScrollTop + this.scrollbarWidth;
                } else if (this.containerCoords.top + elHeight > parentHeight + parentScrollTop) {
                    this.containerCoords.top = parentHeight + parentScrollTop - elHeight - this.scrollbarWidth;
                }
            }

            if (this.isTop || this.isBottom) {
                this.containerCoords.top = this.isTop
                    ? togglerTop - elHeight
                    : togglerTop + togglerHeight;

                if (this.isAxisStart) {
                    this.containerCoords.left = togglerLeft;
                } else if (this.isAxisEnd) {
                    this.containerCoords.left = togglerLeft + togglerWidth - elWidth;
                } else {
                    this.containerCoords.left = togglerLeft + (togglerWidth - elWidth) / 2;
                }

                if (this.containerCoords.left < parentScrollLeft) {
                    this.containerCoords.left = parentScrollLeft + this.scrollbarWidth;
                } else if (this.containerCoords.left + elWidth > parentWidth + parentScrollLeft) {
                    this.containerCoords.left = parentWidth + parentScrollLeft - elWidth - this.scrollbarWidth;
                }
            }
        },
        async activate() {
            await this.$nextTick();

            const parentDOMElement = this.getParentDOMElement();

            if (parentDOMElement) {
                parentDOMElement.appendChild(this.$refs.container);
                this.isDeactivated = false;
            }

            const observableElements = [this.$refs.container];

            if (!this.fixed) {
                observableElements.push(this.$refs.container.parentElement);
            }

            observableElements.forEach(item => {
                if (item) {
                    this.resizeObserver.observe(item);
                }
            });
        },
        /** @public */
        async deactivate() {
            if (!this.initialContainerDOMElement) return;

            await this.$nextTick();
            this.initialContainerDOMElement.appendChild(this.$refs.container);
            this.isDeactivated = true;
            this.resizeObserver.disconnect();
        },
        update() {
            this.resetBoundaries();
            this.calcBoundaries();
        },
    },
    props: {
        /**
         * Устанавливает `position: fixed` для контейнера
         */
        fixed: {
            type: Boolean,
            default: false,
        },
        /**
         * HTML элемент, в котором изначально рендерится `FloatContainer`
         *
         * Если не передан, то будет использован `element.parentElement`
         *
         * Используется для возврата элемента `FloatContainer` в изначальный контейнер
         * после деактивации
         */
        initialContainer: {
            type: [HTMLElement, null],
            default: null,
        },
        /**
         * Отступ между `FloatContainer` и активатором
         */
        offset: {
            type: [Number, String],
            default: 12,
            validator: isValueConvertedToNumber,
        },
        /**
         * Селектор контейнера, куда будет рендериться `FloatContainer`
         *
         * Если не указан, то будет использован `document.body`
         */
        parentSelector: {
            type: String,
            default: '',
        },
        /**
         * Выравнивание `FloatContainer` относительно активатора
         *
         * Валидны следующие значения позиции: `top`, `right`, `bottom`, `left`
         *
         * Также можно указать модификаторы для выравнивания
         * относительно края: `-start`, `-end`
         *
         * Примеры использования: `top`, `left-start`, `bottom-end`
         */
        position: {
            type: String,
            default: AllPositions.BOTTOM,
            validator: isArrayContainsValue(FLOAT_POSITIONS),
        },
        /**
         * Активатор `FloatContainer`, относительно которого он выравнивается
         */
        toggler: {
            type: [HTMLElement, null],
            default: null,
        },
        /**
         * Название класса для анимации открытия/закрытия `FloatContainer`
         */
        transitionName: {
            type: String,
            default: AllTransitions.FADE_DOWN,
            validator: isArrayContainsValue(APP_TRANSITIONS),
        },
        /**
         * Определяет виден ли `FloatContainer`
         * @model
         */
        visible: {
            type: Boolean,
            default: true,
        },
        /**
         * `z-index`, используемый для `FloatContainer`
         */
        zIndex: {
            type: [Number, String],
            default: 100,
            validator: isValueConvertedToNumber,
        },
    },
    watch: {
        async visible(value) {
            if (value && !this.isDeactivated) {
                await this.activate();
                await this.update();
            }
        },
        async winWidth() {
            await this.update();
        },
    },
    created() {
        this.resizeObserver = new ResizeObserver(this.update);
    },
    async mounted() {
        this.scrollbarWidth = getScrollbarWidth();

        if (this.visible && !this.isDeactivated) {
            await this.activate();
        }

        this.calcBoundaries();
    },
    beforeUnmount() {
        this.resizeObserver.disconnect();
    },
};
</script>

<style lang="scss">
.sbs-float-container {
    position: absolute;
    top: 0;
    left: 0;
    transition-property: opacity, transform;

    &.fixed {
        position: fixed;
    }
}
</style>
